/**
 * CryptoUtils.ts
 * ---------------
 * AES-256-GCM encryption/decryption for the token passed in the URL from
 * Project 1 (Digital Employee) to Project 2 (Memory Hub).
 *
 * Both projects must share the SAME 64-character hex key in their .env:
 *   Project 2 (here):  VITE_TOKEN_ENCRYPT_KEY=<64-hex-chars>
 *   Project 1:         REACT_APP_TOKEN_ENCRYPT_KEY=<same 64-hex-chars>
 *                      (or VITE_ prefix if Project 1 also uses Vite)
 *
 * Generate a key (run once in Node):
 *   node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"
 *
 * Security properties:
 *   - AES-256-GCM provides confidentiality AND integrity (authenticated cipher)
 *   - A fresh random 12-byte IV is generated per encryption — same plaintext
 *     produces different ciphertext every time
 *   - The token is stripped from the URL immediately after decryption
 */

const _rawEncryptKey = import.meta.env.VITE_TOKEN_ENCRYPT_KEY as string | undefined;
if (!_rawEncryptKey) {
  throw new Error(
    '[CryptoUtils] VITE_TOKEN_ENCRYPT_KEY is not set. ' +
    'Generate a 64-character hex key and add it to your .env file.'
  );
}
if (!/^[a-fA-F0-9]{64}$/.test(_rawEncryptKey)) {
  throw new Error('[CryptoUtils] VITE_TOKEN_ENCRYPT_KEY must be a 64-character hex string.');
}
const ENCRYPT_KEY_HEX: string = _rawEncryptKey;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(Math.floor(hex.length / 2));
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

async function getKey(): Promise<CryptoKey> {
  const keyBytes = hexToBytes(ENCRYPT_KEY_HEX);
  return crypto.subtle.importKey(
    'raw',
    keyBytes.buffer.slice(keyBytes.byteOffset, keyBytes.byteOffset + keyBytes.byteLength) as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/** base64url → Uint8Array */
function fromBase64Url(b64: string): Uint8Array {
  const standard = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = standard.padEnd(standard.length + ((4 - (standard.length % 4)) % 4), '=');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

/** Uint8Array → base64url (no padding) */
function toBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Encrypt a JWT string for safe URL embedding.
 *
 * Used in PROJECT 1 before building the redirect URL.
 * Result is a base64url-encoded string: [12-byte IV][ciphertext+tag]
 *
 * @example (Project 1)
 *   import { encryptToken } from './cryptoUtils';
 *   const enc = await encryptToken(keycloak.token);
 *   window.open(`http://localhost:3001/app?kc_enc_token=${enc}`, '_blank');
 */
export async function encryptToken(plainToken: string): Promise<string> {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plainToken);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return toBase64Url(combined);
}

/** Ensure a Uint8Array is backed by a plain ArrayBuffer (required by crypto.subtle) */
function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

/**
 * Decrypt the URL token received from Project 1.
 *
 * Supports two formats produced by Project 1:
 *   Format A (dot-separated):  "<iv_b64url>.<ciphertext_b64url>"  ← Project 1 current format
 *   Format B (concatenated):   "<iv+ciphertext as single b64url>" ← legacy Memory Hub format
 *
 * Returns the raw JWT string on success, null on any failure.
 */
export async function decryptUrlToken(encryptedB64: string): Promise<string | null> {
  try {
    const key = await getKey();
    let iv: Uint8Array;
    let ciphertext: Uint8Array;

    if (encryptedB64.includes('.')) {
      // Format A — dot-separated: "<iv_b64url>.<ciphertext_b64url>"
      const [ivB64, ctB64] = encryptedB64.split('.');
      iv = fromBase64Url(ivB64);
      ciphertext = fromBase64Url(ctB64);
    } else {
      // Format B — concatenated single blob: first 12 bytes are IV
      const bytes = fromBase64Url(encryptedB64);
      iv = bytes.slice(0, 12);
      ciphertext = bytes.slice(12);
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(iv) },
      key,
      toArrayBuffer(ciphertext)
    );
    return new TextDecoder().decode(decrypted);
  } catch (err) {
    console.error('[CryptoUtils] Decryption failed — wrong key or corrupted token:', err);
    return null;
  }
}

/**
 * Decrypt the encrypted URL payload from Project 1 (?kc_enc_payload=...).
 *
 * Project 1 sends both the access token AND refresh token as an encrypted
 * JSON object: { token: string; refresh_token: string }
 *
 * URLSearchParams.get() already URL-decodes the value, so pass the raw
 * result of urlParams.get('kc_enc_payload') directly — do NOT call
 * decodeURIComponent() before passing here.
 */
export async function decryptUrlPayload(
  encryptedB64: string
): Promise<{ token: string; refresh_token: string } | null> {
  const json = await decryptUrlToken(encryptedB64);
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as { token?: string; refresh_token?: string };
    if (!parsed.token) {
      console.error('[CryptoUtils] Decrypted payload missing required "token" field.');
      return null;
    }
    return { token: parsed.token, refresh_token: parsed.refresh_token || '' };
  } catch {
    console.error('[CryptoUtils] Failed to parse decrypted payload as JSON.');
    return null;
  }
}
